import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaBars,
  FaUser,
  FaWallet,
  FaChartLine,
  FaClipboardList,
  FaSignOutAlt,
  FaBell,
  FaUserMd
} from 'react-icons/fa';
import { notificationsAPI } from '../../utils/api';
import { sanitizeMarkdownHtml } from '../../utils/sanitizeHtml';

const MAX_VISIBLE_NOTIFICATIONS = 4;

// Функция для парсинга markdown-разметки в простой HTML
const parseMarkdown = (text) => {
  if (!text) return '';
  
  // Сначала экранируем HTML-теги для безопасности
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Применяем форматирование (после экранирования HTML)
  html = html
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')  // Жирный **текст**
    .replace(/\*(.*?)\*/g, '<em>$1</em>')              // Курсив *текст*
    .replace(/~~(.*?)~~/g, '<del>$1</del>')            // Зачеркнутый ~~текст~~
    .replace(/__(.*?)__/g, '<u>$1</u>');               // Подчеркнутый __текст__
  
  // БЕЗОПАСНОСТЬ: Дополнительная санитизация через DOMPurify
  return sanitizeMarkdownHtml(html);
};

const DashboardHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const menuRef = useRef(null);
  const notificationsRef = useRef(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const fetchNotifications = React.useCallback(async () => {
    try {
      setIsLoadingNotifications(true);
      const response = await notificationsAPI.getRecent();
      setNotifications(response.notifications || []);
      setUnreadCount(response.unread || 0);
      setNotificationsError('');
    } catch (error) {
      setNotificationsError(error.message || 'Не удалось загрузить уведомления');
    } finally {
      setIsLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchNotifications();
    };

    window.addEventListener('notifications:refresh', handleRefresh);
    return () => {
      window.removeEventListener('notifications:refresh', handleRefresh);
    };
  }, [fetchNotifications]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleNotifications = () => {
    // На мобильных устройствах сразу переходим на страницу уведомлений
    if (window.innerWidth <= 768) {
      setIsMenuOpen(false);
      navigate('/dashboard/notifications');
      return;
    }
    setIsNotificationsOpen((prev) => !prev);
    setNotificationsError('');
  };

  const handleViewAllNotifications = () => {
    setIsNotificationsOpen(false);
    navigate('/dashboard/notifications');
  };

  const hasNotifications = notifications.length > 0;

  return (
    <header className="dashboard-header">
      <div className="dashboard-header__content">
        <Link to="/" className="dashboard-header__logo">
          <img src={process.env.PUBLIC_URL + '/lagom-life-logo.png'} alt="Lagom Life" style={{ height: '40px', width: 'auto', marginRight: '12px' }} />
          Личный кабинет
        </Link>
        
        <nav className="dashboard-header__nav">
          <Link to="/dashboard/team" className="dashboard-header__nav-link">Команда</Link>
          <Link to="/marketing" className="dashboard-header__nav-link">Маркетинг</Link>
          <Link to="/dashboard/rewards-report" className="dashboard-header__nav-link">Вознаграждения</Link>
          <Link to="/dashboard/referral-program" className="dashboard-header__nav-link">Реферальная программа</Link>
        </nav>
        
        <div className="dashboard-header__user-section">
          <div ref={notificationsRef} className="notifications-wrapper">
            <button
              className={`icon-button notifications-trigger ${isNotificationsOpen ? 'is-active' : ''}`}
              onClick={toggleNotifications}
              aria-label="Уведомления"
            >
              <FaBell />
              {unreadCount > 0 && (
                <span className="notifications-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <div className={`notifications-popover ${isNotificationsOpen ? 'notifications-popover--open' : ''}`}>
              <div className="notifications-popover__header">
                <span>Уведомления</span>
                {isLoadingNotifications && <span className="notifications-popover__loading">Загрузка...</span>}
              </div>

              <div className="notifications-popover__list">
                {notificationsError ? (
                  <div className="notifications-popover__empty">
                    {notificationsError}
                  </div>
                ) : hasNotifications ? (
                  notifications.slice(0, MAX_VISIBLE_NOTIFICATIONS).map((notification) => (
                    <div
                      key={notification.id}
                      className={`notifications-popover__item notifications-popover__item--${notification.status}`}
                    >
                      <div className="notifications-popover__item-title">
                        {notification.title}
                      </div>
                      <div 
                        className="notifications-popover__item-message"
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(notification.message) }}
                      />
                      <div className="notifications-popover__item-date">
                        {new Date(notification.created_at).toLocaleString('ru-RU', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="notifications-popover__empty">
                    Пока нет уведомлений
                  </div>
                )}
              </div>

              <button
                className="notifications-popover__footer"
                onClick={handleViewAllNotifications}
              >
                Все уведомления
              </button>
            </div>
          </div>

          <div ref={menuRef} style={{ position: 'relative' }}>
            <button 
              className="icon-button" 
              onClick={toggleMenu}
              style={{ 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FaBars style={{ fontSize: '18px' }} />
            </button>
            <div 
              className={`dropdown-menu ${isMenuOpen ? 'dropdown-menu--open' : ''}`}
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                minWidth: '200px',
                zIndex: 1000,
                overflow: 'hidden',
                transformOrigin: 'top right',
                transform: isMenuOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(-10px)',
                opacity: isMenuOpen ? '1' : '0',
                visibility: isMenuOpen ? 'visible' : 'hidden',
                transition: 'all 0.2s ease'
              }}
            >
              <div className="mobile-nav-links" style={{ display: 'none' }}>
                <Link 
                  to="/dashboard/team"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Команда
                </Link>
                <Link 
                  to="/marketing"
                  style={{
                    width: '100%',
                    display: 'flex',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    textDecoration: 'none',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Маркетинг
                </Link>
                <Link 
                  to="/dashboard/rewards-report"
                  style={{
                    width: '100%',
                    display: 'flex',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    textDecoration: 'none',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Вознаграждения
                </Link>
                <Link 
                  to="/dashboard/referral-program"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Реферальная программа
                </Link>
              </div>
              <Link 
                to="/dashboard"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                <FaUser style={{ fontSize: '14px' }} />
                Личный кабинет
              </Link>
              <button 
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'none',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                <FaWallet style={{ fontSize: '14px' }} />
                Мой кошелек
              </button>
              <Link 
                to="/dashboard/finances"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                <FaChartLine style={{ fontSize: '14px' }} />
                Мои финансы
              </Link>
              <Link 
                to="/dashboard/my-programs"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                <FaClipboardList style={{ fontSize: '14px' }} />
                Мои программы
              </Link>
              <Link 
                to="/dashboard/expert"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                <FaUserMd style={{ fontSize: '14px' }} />
                Кабинет эксперта
              </Link>
              <Link 
                to="/dashboard/orders-history"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                <FaClipboardList style={{ fontSize: '14px' }} />
                История заказов
              </Link>
              <button 
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#3B82F6',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onClick={handleLogout}
              >
                <FaSignOutAlt style={{ fontSize: '14px' }} />
                Выйти
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader; 